import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Snackbar,
  Alert,
  Badge,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { BugReport, ExpandMore, ContentCopy } from '@mui/icons-material';

const ConsoleCapture = () => {
  const [hasErrors, setHasErrors] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    // Set up custom alert function
    window.showConsoleAlert = message => {
      setAlertMessage(message);
      setShowAlert(true);
    };

    // Check for errors periodically
    const checkForErrors = () => {
      try {
        if (window.consoleCapture?.logs?.length > 0) {
          // Count errors and warnings
          const errors = window.consoleCapture.logs.filter(
            log => log.type === 'ERROR' || log.type === 'WARN'
          );
          const totalLogs = window.consoleCapture.logs.length;

          setErrorCount(errors.length);
          setTotalCount(totalLogs);

          // Show button if there are any console messages
          setHasErrors(totalLogs > 0);
        }
      } catch (error) {
        // Fallback: use original console if available
        if (window.consoleCapture?.originalMethods?.error) {
          window.consoleCapture.originalMethods.error('Error in ConsoleCapture:', error);
        } else {
          console.error('Error in ConsoleCapture:', error);
        }
      }
    };

    // Check immediately and then every 2 seconds
    checkForErrors();
    const interval = setInterval(checkForErrors, 2000);

    return () => {
      clearInterval(interval);
      if (window.showConsoleAlert) {
        delete window.showConsoleAlert;
      }
    };
  }, []);

  const handleCopyConsole = async (errorsOnly = false) => {
    setAnchorEl(null); // Close the menu

    try {
      if (window.copyAllConsole) {
        await window.copyAllConsole(errorsOnly);
      } else {
        // Fallback if the copy function isn't available
        const logs = window.consoleCapture?.logs || [];
        const filteredLogs = errorsOnly
          ? logs.filter(log => log.type === 'ERROR' || log.type === 'WARN')
          : logs;

        const currentUrl = window.location.href;
        const referrer = document.referrer || 'Direct navigation';
        const timestamp = new Date().toISOString();

        const header = `=== CONSOLE OUTPUT CAPTURED ===
This console was copied from: ${currentUrl}
Referrer page: ${referrer}
Captured at: ${timestamp}
Filter: ${errorsOnly ? 'Errors and Warnings Only' : 'All Console Messages'}
Total messages: ${filteredLogs.length}
=======================================

`;

        const logOutput = filteredLogs
          .map(log => `[${log.timestamp}] ${log.type}: ${log.message}`)
          .join('\n');

        const output = header + logOutput;

        await navigator.clipboard.writeText(output);
        const message = errorsOnly
          ? `${filteredLogs.length} errors/warnings copied to clipboard!`
          : `${filteredLogs.length} console messages copied to clipboard!`;
        setAlertMessage(message);
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Failed to copy console output:', error);
      setAlertMessage('Failed to copy console output');
      setShowAlert(true);
    }
  };

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  // Show the button if there are any console messages
  if (!hasErrors) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Tooltip
          title={
            errorCount > 0
              ? `${errorCount} console errors/warnings`
              : `${totalCount} console messages`
          }
        >
          <Badge
            badgeContent={errorCount > 0 ? errorCount : totalCount}
            color={errorCount > 0 ? 'error' : 'primary'}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                right: -3,
                top: 13,
              },
            }}
          >
            <IconButton
              onClick={isHovered ? handleMenuClick : () => handleCopyConsole(errorCount > 0)}
              sx={{
                backgroundColor: errorCount > 0 ? '#d32f2f' : '#1976d2',
                color: 'white',
                width: isHovered ? 'auto' : 48,
                height: 48,
                borderRadius: isHovered ? 2 : '50%',
                boxShadow: 3,
                transition: 'all 0.3s ease-in-out',
                minWidth: 48,
                padding: isHovered ? '8px 16px' : '12px',
                '&:hover': {
                  backgroundColor: errorCount > 0 ? '#b71c1c' : '#1565c0',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <BugReport sx={{ fontSize: 24 }} />
              {isHovered && (
                <Box
                  sx={{
                    ml: 1,
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem',
                  }}
                >
                  {errorCount > 0 ? 'Copy Errors' : 'Copy Console'}
                  <ExpandMore sx={{ ml: 0.5, fontSize: 20 }} />
                </Box>
              )}
            </IconButton>
          </Badge>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleCopyConsole(true)} disabled={errorCount === 0}>
          Copy Errors & Warnings Only ({errorCount})
        </MenuItem>
        <MenuItem onClick={() => handleCopyConsole(false)}>
          Copy All Messages ({totalCount})
        </MenuItem>
      </Menu>

      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity='success'>
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ConsoleCapture;
